import { c as _c } from "react/compiler-runtime";
import { Avatar, menuItemVariants, Popover } from '@systemeio/components';
import { ChevronDownIcon, LogOut01Icon, Settings02Icon } from '@systemeio/icons';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import { ProfileSkeleton } from 'shared/components/navbar/components/navbar-skeleton';
import { SubaccountSelector } from 'shared/components/navbar/components/subaccount-selector';
import { WorkspaceSelector } from 'shared/components/navbar/components/workspace-selector';
import { DESKTOP_NAVBAR_CHEVRON_CLASS_NAME, MENU_POPUP_SEPARATOR_CLASS_NAME, NAVBAR_HOVER_OPEN_DELAY, NAVBAR_PANEL_CLASS_NAME } from 'shared/components/navbar/constants';
import { focusPanelOnKeyboardOpenOnly } from 'shared/components/navbar/utils/focus-panel-on-keyboard-open-only';
import { pageRoute } from 'shared/constants/routes.constants';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { useRouter } from 'shared/hooks/use-router';
import useUser from 'shared/hooks/use-user';
import { getAccountAvatarName } from 'shared/hooks/use-user/utils/get-account-avatar-name';
import { hasWorkspaces } from 'shared/hooks/use-user/utils/has-workspaces';
import { getPageRoute } from 'shared/utils/get-dynamic-page-route';
import { twJoin, twMerge } from 'tailwind-merge';

/**
 * A `Popover`, not a `Menu`: the panel carries two account selectors, and a menu popup focuses its
 * first tabbable control on every open — which put a focus ring on a selector the user only
 * hovered. Rows keep the menu look through `menuItemVariants` and close the panel through
 * `Popover.Close`, the one menu-item behaviour worth keeping here.
 */
export function ProfileMenu() {
  const $ = _c(40);
  const {
    avatarUrl,
    dashboardUser,
    isDisabled,
    isLoading,
    isSupportImpersonation
  } = useUser();
  const {
    pathname
  } = useRouter();
  const {
    t
  } = useLocoTranslation();
  let t0;
  if ($[0] !== pathname) {
    t0 = pathname.includes("/profile");
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const profileIsActive = t0;
  if (isLoading) {
    let t1;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <ProfileSkeleton />;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  if (!dashboardUser) {
    return null;
  }
  const t1 = `${dashboardUser.firstName ?? ""} ${dashboardUser.lastName ?? ""}`;
  let t2;
  if ($[3] !== t1) {
    t2 = t1.trim();
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const displayName = t2;
  let t3;
  if ($[5] !== dashboardUser) {
    t3 = getAccountAvatarName(dashboardUser);
    $[5] = dashboardUser;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== avatarUrl || $[8] !== displayName || $[9] !== t3) {
    t4 = <Avatar alt={displayName} size="lg" data-test-id="top-menu-avatar" {...t3} src={avatarUrl} />;
    $[7] = avatarUrl;
    $[8] = displayName;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ChevronDownIcon className={twJoin("transition-base size-5", DESKTOP_NAVBAR_CHEVRON_CLASS_NAME)} />;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== displayName || $[13] !== t4) {
    t6 = <Popover.Trigger aria-label={displayName} className="text-icon-primary transition-base hover:bg-bg-tertiary data-popup-open:bg-bg-tertiary relative flex items-center justify-center gap-0.5 rounded-md py-1 pr-1 pl-2 transition-colors data-popup-open:[&>svg]:rotate-180" data-test-id="profile-menu-button" delay={NAVBAR_HOVER_OPEN_DELAY} openOnHover={true}>{t4}{t5}</Popover.Trigger>;
    $[12] = displayName;
    $[13] = t4;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = twJoin(NAVBAR_PANEL_CLASS_NAME, "w-60 max-w-[calc(100vw-2rem)] py-1");
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== dashboardUser || $[17] !== isDisabled || $[18] !== profileIsActive || $[19] !== t) {
    t8 = !isDisabled && <><SubaccountSelector isDesktop={true} /><Popover.Close className={twMerge(menuItemVariants({
        state: profileIsActive ? "active" : "default"
      }), "gap-1.5")} render={<LinkWithoutPrefetch aria-current={profileIsActive ? "page" : undefined} data-test-id="profile-settings-button" href={getPageRoute(pageRoute.ProfileUserSettings)} />}><Settings02Icon /><span className="min-w-0 truncate">{t("global.settings")}</span></Popover.Close>{hasWorkspaces(dashboardUser) && <><PanelSeparator /><WorkspaceSelector isDesktop={true} /></>}<PanelSeparator /></>;
    $[16] = dashboardUser;
    $[17] = isDisabled;
    $[18] = profileIsActive;
    $[19] = t;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== isSupportImpersonation || $[22] !== t) {
    t9 = isSupportImpersonation && <a className={twMerge(menuItemVariants({
      state: "destructive"
    }), "gap-1.5")} data-test-id="exit-impersonation-button" href="/dashboard/back/entry?pretend_this_user=_exit"><LogOut01Icon /><span className="min-w-0 truncate">{t("header.exit_impersonation")}</span></a>;
    $[21] = isSupportImpersonation;
    $[22] = t;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = twMerge(menuItemVariants({
      state: "destructive"
    }), "gap-1.5");
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <LogOut01Icon />;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t) {
    t12 = t("header.user.profile.menu.logout");
    $[26] = t;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t12) {
    t13 = <span className="min-w-0 truncate">{t12}</span>;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t10 || $[31] !== t13) {
    t14 = <a className={t10} data-test-id="logout-button" href="/dashboard/logout">{t11}{t13}</a>;
    $[30] = t10;
    $[31] = t13;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t14 || $[34] !== t8 || $[35] !== t9) {
    t15 = <Popover.Portal><Popover.Positioner align="end" className="z-60" sideOffset={4}><Popover.Popup className={t7} initialFocus={focusPanelOnKeyboardOpenOnly}>{t8}{t9}{t14}</Popover.Popup></Popover.Positioner></Popover.Portal>;
    $[33] = t14;
    $[34] = t8;
    $[35] = t9;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t15 || $[38] !== t6) {
    t16 = <Popover.Root>{t6}{t15}</Popover.Root>;
    $[37] = t15;
    $[38] = t6;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  return t16;
}
function PanelSeparator() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div aria-hidden={true} className={twJoin("bg-border-100 h-px shrink-0", MENU_POPUP_SEPARATOR_CLASS_NAME)} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}